import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "../types/database";

export type DatabaseReadinessStatus =
  "ready" | "schema-unavailable" | "network-error";
export type DatabaseReadinessResult = {
  status: DatabaseReadinessStatus;
  checkedTables: string[];
  safeMessage?: string;
};

const requiredTables = [
  "profiles",
  "roles",
  "user_roles",
  "authorized_superusers",
  "audit_events",
] as const;

function isSchemaMissing(status?: number, message = ""): boolean {
  return (
    status === 404 ||
    /could not find|does not exist|schema cache|relation .* does not exist/i.test(
      message,
    )
  );
}

export async function checkDatabaseReadiness(
  supabase: SupabaseClient<Database>,
): Promise<DatabaseReadinessResult> {
  for (const table of requiredTables) {
    const { error } = await (
      supabase.from(table).select("*").limit as unknown as (
        count: number,
      ) => Promise<{
        error: { message?: string; code?: string; status?: number } | null;
      }>
    )(1);
    if (error) {
      if (isSchemaMissing(error.status, error.message)) {
        return {
          status: "schema-unavailable",
          checkedTables: [...requiredTables],
          safeMessage: "Unable to sign in",
        };
      }
      return {
        status: "network-error",
        checkedTables: [...requiredTables],
        safeMessage: "Unable to sign in",
      };
    }
  }

  return { status: "ready", checkedTables: [...requiredTables] };
}
